import { defineStore } from 'pinia';
import { updatePreferences } from '@/api/auth';

// 全局共享的「AI 生成偏好」：选中的平台、知识库开关、手动调过的温度/最大长度
// AI 内容生成页、关键词拓展弹窗、批量生成弹窗三处的选择互相同步：
// 任何一处改了平台，其他地方下次打开都跟随这个选择
// 持久化到数据库（user.extra_data.preferences.ai），换电脑也跟随账号；
// localStorage 只作秒开镜像（数据库读出前先顶上），数据库值为准
const BASE_KEY = 'ai_selected_model_id';
const KNOWLEDGE_KEY = 'ai_knowledge_on';
// 兼容旧版只存在于 AI 内容生成页的缓存 key
const LEGACY_KEY = 'ai_generate_model_id';

export const useAiModelStore = defineStore('aiModel', {
    state: () => ({
        // 当前偏好归属的账号 id（登录/拉取用户信息后由 user store 写入）
        ownerId: null,
        selectedModelId: '',
        knowledgeOn: true,
        // 用户手动调过的生成参数（null = 未覆盖，跟随模型默认值）
        temperature: null,
        maxTokens: null
    }),
    getters: {
        cacheKey: (s) => (s.ownerId ? `${BASE_KEY}:u${s.ownerId}` : BASE_KEY),
        knowledgeKey: (s) => (s.ownerId ? `${KNOWLEDGE_KEY}:u${s.ownerId}` : KNOWLEDGE_KEY)
    },
    actions: {
        // 登录/切换账号/刷新用户信息后调用：绑定账号并载入该账号的偏好（数据库为准，本地镜像兜底）
        bindUser(user) {
            const uid = user?.id || null;
            this.ownerId = uid;
            const prefs = user?.extraData?.preferences?.ai || {};
            this.selectedModelId = prefs.modelId
                ? String(prefs.modelId)
                : (localStorage.getItem(this.cacheKey) || localStorage.getItem(LEGACY_KEY) || '');
            this.knowledgeOn = prefs.knowledgeOn !== undefined
                ? !!prefs.knowledgeOn
                : localStorage.getItem(this.knowledgeKey) !== '0';
            this.temperature = prefs.temperature ?? null;
            this.maxTokens = prefs.maxTokens ?? null;
            this.mirrorLocal();
        },
        // 本地镜像：页面秒开时先用它渲染，等 /me 回来再以数据库为准
        mirrorLocal() {
            if (this.selectedModelId) {
                localStorage.setItem(this.cacheKey, this.selectedModelId);
            } else {
                localStorage.removeItem(this.cacheKey);
            }
            localStorage.setItem(this.knowledgeKey, this.knowledgeOn ? '1' : '0');
            localStorage.removeItem(LEGACY_KEY);
        },
        // 写库（静默失败：网络异常时本地镜像仍生效，下次登录会同步）
        syncRemote() {
            if (!this.ownerId) return;
            updatePreferences({
                ai: {
                    modelId: this.selectedModelId || null,
                    knowledgeOn: this.knowledgeOn,
                    temperature: this.temperature,
                    maxTokens: this.maxTokens
                }
            }).catch(() => {});
        },
        setSelected(id) {
            this.selectedModelId = id ? String(id) : '';
            this.mirrorLocal();
            this.syncRemote();
        },
        // 记住「引用企业知识库」开关
        setKnowledgeOn(v) {
            this.knowledgeOn = !!v;
            this.mirrorLocal();
            this.syncRemote();
        },
        // 记住手动调过的温度/最大长度（null 表示清除覆盖，回到跟随模型默认值）
        setParams(temperature, maxTokens) {
            this.temperature = temperature ?? null;
            this.maxTokens = maxTokens ?? null;
            this.syncRemote();
        },
        // 在给定候选列表里解析应选中的模型：缓存命中优先，已停用/删除时回退第一个
        resolve(candidates) {
            if (!candidates?.length) return null;
            return candidates.find(m => String(m.id) === this.selectedModelId) || candidates[0];
        }
    }
});
